import { useEffect, useState, useContext, useMemo } from "react";
import {
  Form,
  Input,
  Divider,
  Checkbox,
  Row,
  Col,
  Select,
  InputNumber,
  message,
  Button,
  Tooltip,
  Table,
} from "antd";
import RecipientForm from "./recipient";
import Receiver from "./receiver";
import { OptionSelect } from "@MASTER";
import { AppContext, AuthContext } from "@CONTEXTS";
import { moment } from "@LIBS";
import { TiContacts } from "react-icons/ti";
import {
  FaEdit,
  FaRegArrowAltCircleRight,
  FaRegTimesCircle,
} from "react-icons/fa";
import ReceiverInfoModal from "../../../modules/create/delivery/receiver-info-modal";
import ProductModal from "../../../modules/create/delivery/product-modal";
import HJSearchSelect from "../search-select";
import { ProductEndpoints } from "@PRODUCTS";
import { ReceiverEndpoints } from "@RECEIVERS";
import {
  getCustomField,
  addProductToDeliveryForm,
  applyReceiverToForm,
  removeProductFromDeliveryForm,
  normalizeDeliveryProducts,
} from "@HELPERS";

const { Option } = Select;
const turkishCharacters = [
  "ğ",
  "Ğ",
  "ı",
  "İ",
  "ş",
  "Ş",
  "ü",
  "Ü",
  "ö",
  "Ö",
  "ç",
  "Ç",
];

const renderTruncatedText = (text, maxLength = 40) => {
  if (text && text.length > maxLength) {
    return (
      <Tooltip title={text} placement="topLeft">
        {`${text.substring(0, maxLength)}...`}
      </Tooltip>
    );
  }
  return text;
};

const CreateDeliveryForm = ({
  fieldName,
  form,
  initValues,
  warehouseSelect,
  autoDeliveryNumber,
}) => {
  const [visibleReceiver, setVisibleReceiver] = useState(false);
  const { options } = useContext(AppContext);
  const [deliveryType, setDeliveryType] = useState(
    options?.DELIVERY_TYPES.filter((x) => x.key === "HX_STD")?.length > 0
      ? "HX_STD"
      : options?.DELIVERY_TYPES[0]?.key
  );
  const [desi, setDesi] = useState(null);
  const [receiverInfoModal, setReceiverInfoModal] = useState(false);
  const [productModal, setProductModal] = useState(false);

  const [editable, setEditable] = useState(false);
  const { info } = useContext(AuthContext);

  useEffect(() => {
    const values = initValues || form.getFieldsValue()[fieldName];

    if (values?.receiverName && values.receiverName !== values?.recipientName) {
      setVisibleReceiver(true);
    } else {
      setVisibleReceiver(false);
    }
    setDesi(
      values?.desi
        ? values.desi
        : options["DESI_RETAIL"]?.length > 0
        ? options["DESI_RETAIL"][0]?.key
        : null
    );

    form.setFieldsValue({
      [fieldName]: {
        desi: values?.desi
          ? values.desi
          : options["DESI_RETAIL"]?.length > 0
          ? options["DESI_RETAIL"][0]?.key
          : null,
        deliveryType: values?.deliveryType
          ? values.deliveryType
          : options?.DELIVERY_TYPES.filter((x) => x.key === "HX_STD")?.length >
            0
          ? "HX_STD"
          : options?.DELIVERY_TYPES[0]?.key,
        totalParcels: values?.totalParcels ? values.totalParcels : 1,
        products: normalizeDeliveryProducts(values),
      },
    });

    if (values?.deliveryType) {
      setDeliveryType(values.deliveryType);
    }
  }, [fieldName, form, initValues]);

  const openReceiverInfoModal = async () => {
    if (!info.authorities.includes("RECEIVER_LIST")) {
      return message.error("Kişileri görüntüleme yetkiniz bulunmamaktadır!");
    }
    setReceiverInfoModal(true);
  };

  const customFields = useMemo(() => {
    return info?.customFields?.filter((x) =>
      x.deliveryType.includes("DELIVERY")
    );
  }, [info]);
  const openProductModal = async () => {
    setProductModal(true);
  };

  if (!form) return "form eksik.";

  return (
    <>
      {receiverInfoModal && (
        <ReceiverInfoModal
          onClose={() => setReceiverInfoModal(false)}
          fieldName={fieldName}
          form={form}
        />
      )}
      {productModal && (
        <ProductModal
          onClose={() => setProductModal(false)}
          fieldName={fieldName}
          form={form}
          setDeliveryType={setDeliveryType}
          setDesi={setDesi}
        />
      )}
      <Row gutter={24}>
        {warehouseSelect && (
          <Col span={24}>
            <Form.Item
              name={[fieldName, "warehouse"]}
              label="Çıkış Deposu"
              rules={[
                {
                  required: true,
                  message: "Lütfen tüm gönderiler için çıkış deposunu seçiniz.",
                },
              ]}
            >
              <OptionSelect name="WAREHOUSES" allowClear={false} />
            </Form.Item>
          </Col>
        )}

        <Col
          span={
            (deliveryType === "TMH" || deliveryType === "POD") &&
            autoDeliveryNumber
              ? 6
              : deliveryType === "TMH" || deliveryType === "POD"
              ? 7
              : autoDeliveryNumber
              ? 7
              : 8
          }
        >
          <Form.Item
            label="Sipariş Numarası:"
            name={[fieldName, "customerDeliveryNo"]}
            rules={[
              { required: true, message: "Lütfen sipariş numarası giriniz" },
              {
                min: 5,
                max: 16,
                message:
                  "Lütfen geçerli bir sipariş numarası giriniz (5-16 karakter arası).",
              },
            ]}
            initialValue={
              autoDeliveryNumber
                ? info.companyName
                    .replace(" ", "")
                    .replace(
                      new RegExp("[" + turkishCharacters.join("") + "]", "g"),
                      ""
                    )
                    .slice(0, 3) +
                  Math.floor(
                    Math.random() * Math.floor(Math.random() * new Date())
                  )
                : null
            }
          >
            {autoDeliveryNumber && !editable ? (
              <Input maxLength={16} disabled={true} />
            ) : (
              <Input maxLength={16} />
            )}
          </Form.Item>
        </Col>
        {autoDeliveryNumber ? (
          <Col span={1}>
            <Form.Item
              label=""
              name={[fieldName, "customerDeliveryNoEdit"]}
              style={{ marginTop: 30 }}
            >
              <Tooltip
                placement="top"
                title={
                  editable
                    ? "Düzenlemeyi kapamak için tıklayınız!"
                    : "Sistem tarafından otomatik atanan sipariş numarasını değiştirmek için tıklayınız!"
                }
              >
                <Button
                  type="text"
                  icon={editable ? <FaRegArrowAltCircleRight /> : <FaEdit />}
                  onClick={() => setEditable(!editable)}
                ></Button>
              </Tooltip>
            </Form.Item>
          </Col>
        ) : null}
        <Col span={deliveryType === "POD" ? 4 : 6}>
          <Form.Item
            label="Teslimat Türü:"
            name={[fieldName, "deliveryType"]}
            rules={[
              {
                required: true,
                message: "Lütfen teslimat türünü seçiniz",
              },
            ]}
          >
            <OptionSelect
              name="DELIVERY_TYPES"
              onChange={(val) => {
                let tempForm = form.getFieldsValue();
                tempForm[fieldName].slot = null;
                tempForm[fieldName].deliveryAmount = null;
                const selectedProducts = tempForm[fieldName]?.products || [];
                tempForm[fieldName].totalParcels =
                  info?.specialTags?.includes("B2BPRO") &&
                  selectedProducts.length
                    ? selectedProducts.length
                    : 1;
                if (val === "POD") {
                  tempForm[fieldName].desi = options["DESI_POD"][0]?.key;
                } else if (val === "TMH") {
                  tempForm[fieldName].desi = options["DESI_TMH"][0]?.key;
                  setDesi(options["DESI_TMH"][0]?.key);
                } else {
                  tempForm[fieldName].desi = options["DESI_RETAIL"][0]?.key;
                }
                form.setFieldsValue(tempForm);
                setDeliveryType(val);
              }}
            />
          </Form.Item>
        </Col>
        {deliveryType === "POD" ? (
          <>
            <Col span={4}>
              <Form.Item
                name={[fieldName, "deliveryAmount"]}
                label="Tahsil Edilecek Tutar:"
                rules={[
                  {
                    required: true,
                    message: "Lütfen ürün bedelini giriniz",
                  },
                  () => ({
                    validator(_, value) {
                      if (value > 7000) {
                        return Promise.reject(
                          new Error(
                            "Maksimum tahsil edilebilecek tutar 7000 TL’dir"
                          )
                        );
                      }
                      if (value < 0) {
                        return Promise.reject(
                          new Error(
                            "Minimum tahsil edilebilecek tutar 0 TL’dir"
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber style={{ width: "100%" }} decimalSeparator={","} />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item
                name={[fieldName, "paymentType"]}
                label="Ödeme Şekli:"
                rules={[
                  {
                    required: true,
                    message: "Lütfen ödeme şeklini seçiniz",
                  },
                ]}
              >
                <Select>
                  <Option value={"cash"}>Nakit</Option>
                </Select>
              </Form.Item>
            </Col>
          </>
        ) : deliveryType !== "HX_STD" &&
          deliveryType !== "TMH" &&
          deliveryType !== "POD" ? (
          <Col span={4}>
            <Form.Item
              label="Teslimat Saati Aralığı:"
              name={[fieldName, "slot"]}
              rules={[
                {
                  required:
                    deliveryType === "HX_STD" || deliveryType === "TMH"
                      ? false
                      : true,
                  message: "Lütfen teslimat saat aralığını seçiniz",
                },
              ]}
            >
              <Select
                disabled={deliveryType === "HX_STD" || deliveryType === "TMH"}
              >
                <Option disabled={deliveryType === "HX_SD"} value={1}>
                  09:00 - 13:00
                </Option>
                <Option
                  disabled={
                    deliveryType === "HX_SD" &&
                    moment(new Date(), "HH:mm").format("HH") >= 17
                  }
                  value={2}
                >
                  13:00 - 18:00
                </Option>
                <Option
                  disabled={
                    deliveryType === "HX_SD" &&
                    moment(new Date(), "HH:mm").format("HH") >= 22
                  }
                  value={3}
                >
                  18:00 - 23:00
                </Option>
              </Select>
            </Form.Item>
          </Col>
        ) : null}

        <Col span={deliveryType === "POD" ? 3 : deliveryType === "TMH" ? 4 : 6}>
          <Form.Item
            label="Desi Aralığı:"
            name={[fieldName, "desi"]}
            rules={[
              {
                required: true,
                message: "Lütfen desi değerini giriniz",
              },
            ]}
          >
            <OptionSelect
              name={
                deliveryType === "POD"
                  ? "DESI_POD"
                  : deliveryType === "TMH"
                  ? "DESI_TMH"
                  : "DESI_RETAIL"
              }
              onChange={(val) => {
                let tempForm = form.getFieldsValue();
                tempForm[fieldName]["manuelDesi"] = null;
                tempForm[fieldName]["recipientTown"] = null;
                tempForm[fieldName]["recipientDistrict"] = null;

                form.setFieldsValue(tempForm);
                setDesi(val);
              }}
            />
          </Form.Item>
        </Col>

        {deliveryType === "TMH" && (
          <Col span={4}>
            <Form.Item
              label="Desi Bilgisi:"
              name={[fieldName, "manuelDesi"]}
              rules={[
                {
                  required: true,
                  message: "Lütfen desi bilgisini giriniz",
                },
                () => ({
                  validator(_, value) {
                    if (value === null || value === undefined || value === "") {
                      return Promise.resolve();
                    }

                    const numericValue = Number(
                      String(value).replace(",", ".")
                    );
                    if (Number.isNaN(numericValue)) {
                      return Promise.reject("Lütfen geçerli bir desi giriniz");
                    }

                    const selectedDesi =
                      form.getFieldValue([fieldName, "desi"]) ?? desi;
                    const selectedRange = options["DESI_TMH"]?.find(
                      (x) => Number(x.key) === Number(selectedDesi)
                    );
                    const [min, max] = (selectedRange?.tr || "")
                      .split("-")
                      .map((part) => Number(String(part).trim()));

                    if (
                      Number.isNaN(min) ||
                      Number.isNaN(max) ||
                      numericValue < min ||
                      numericValue > max
                    ) {
                      return Promise.reject(
                        `Desi değeri ${min} ile ${max} arasında olmalıdır`
                      );
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                formatter={(value) => value?.toString().replace(".", ",")}
                parser={(value) => value?.replace(",", ".")}
              />
            </Form.Item>
          </Col>
        )}

        <Col span={deliveryType === "TMH" || deliveryType === "POD" ? 3 : 4}>
          <Form.Item
            label="Parça Sayısı:"
            // initialValue={updateModal?.totalParcels}
            name={[fieldName, "totalParcels"]}
            rules={[
              {
                required: true,
                message: "Lütfen parça sayısını giriniz",
              },
            ]}
          >
            <Select
              showSearch
              disabled={
                info?.productSelection &&
                info?.specialTags?.includes("B2BPRO")
              }
            >
              {Array.from(
                {
                  length:
                    deliveryType === "TMH" ||
                    info?.specialTags?.includes("B2B") ||
                    info?.specialTags?.includes("B2BPRO")
                      ? 60
                      : 10,
                },
                (_, i) => i + 1
              ).map((item) => (
                <Option key={item} value={item}>
                  {item}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      {customFields && customFields?.length > 0 && (
        <Row gutter={16}>
          {customFields.map((item) => (
            <Col span={4} key={item.name}>
              {getCustomField({ item, fieldName, form })}
            </Col>
          ))}
        </Row>
      )}
      {deliveryType === "HX_SD" && (
        <Row>
          <p className="dashboard-red-text">
            Bugün teslimat gönderilerinde kargo kabulü ile teslim slotu
            güncellenebilir.
          </p>
        </Row>
      )}
      {deliveryType === "POD" && (
        <Row>
          <p style={{ color: "gray" }}>
            Hepsijet hizmet ve kargo bedeli bu tutardan tahsil edilecektir.
          </p>
        </Row>
      )}
      {info?.productSelection && (
        <div>
          <Divider style={{ marginTop: 0 }} />
          <h4 style={{ marginTop: 4 }}>Malzeme / Ürün Seçimi:</h4>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              marginBottom: 12,
            }}
          >
            <div style={{ flex: 1, maxWidth: 420 }}>
              <HJSearchSelect
                url={ProductEndpoints.listProducts}
                placeholder="Kod veya ürün adı ile malzeme ara"
                emptyText="Kayıtlı malzeme/ürün bulunamadı."
                renderOption={(row) =>
                  `${row?.code || ""} ${row?.name || ""}`.trim()
                }
                onSelect={(record) =>
                  addProductToDeliveryForm({
                    form,
                    fieldName,
                    record,
                    desiOptions: options["DESI_TMH"],
                    setDeliveryType,
                    setDesi,
                    syncTotalParcels: info?.specialTags?.includes("B2BPRO"),
                  })
                }
              />
            </div>
            <Button
              size="small"
              type="link"
              onClick={() => openProductModal()}
              icon={<TiContacts />}
              style={{ margin: 0, padding: 0 }}
            >
              Kayıtlı Malzeme / Ürünlerim
            </Button>
          </div>

          <Form.Item shouldUpdate noStyle>
            {() => {
              const values = form.getFieldsValue(true);
              const selectedProducts = values?.[fieldName]?.products || [];
              const totalDesi = selectedProducts.reduce(
                (sum, product) => sum + Number(product?.desi || 0),
                0
              );

              return selectedProducts.length > 0 ? (
                <Table
                  size="small"
                  style={{ marginBottom: 10 }}
                  columns={[
                    {
                      title: "Kod",
                      dataIndex: "code",
                      key: "code",
                      ellipsis: true,
                      render: (code) => renderTruncatedText(code),
                    },
                    {
                      title: "Ürün",
                      dataIndex: "name",
                      key: "name",
                      ellipsis: true,
                      render: (name) => renderTruncatedText(name),
                    },
                    {
                      title: "Desi",
                      dataIndex: "desi",
                      key: "desi",
                      width: 80,
                    },
                    {
                      title: "",
                      key: "action",
                      width: 48,
                      align: "center",
                      render: (_, __, index) => (
                        <Button
                          icon={
                            <FaRegTimesCircle style={{ margin: 0, top: 2 }} />
                          }
                          type="text"
                          danger
                          onClick={() => {
                            removeProductFromDeliveryForm({
                              form,
                              fieldName,
                              index,
                              desiOptions: options["DESI_TMH"],
                              setDeliveryType,
                              setDesi,
                              syncTotalParcels:
                                info?.specialTags?.includes("B2BPRO"),
                            });
                          }}
                        />
                      ),
                    },
                  ]}
                  dataSource={selectedProducts}
                  rowKey={(record, index) =>
                    `${record?.id || record?._id}-${index}`
                  }
                  pagination={false}
                  scroll={{ y: 300 }}
                  summary={() => (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={2}>
                          <b>Toplam Desi</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>
                          <b>{totalDesi}</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} />
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                />
              ) : null;
            }}
          </Form.Item>

          <Form.Item
            name={[fieldName, "products"]}
            rules={[
              {
                validator: (_, value) => {
                  if (
                    info?.productSelectionRequired &&
                    (!value || value.length === 0)
                  ) {
                    return Promise.reject("Lütfen malzeme/ürün seçiniz!");
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <div />
          </Form.Item>
        </div>
      )}
      <Divider style={{ marginTop: 0 }} />
      <div>
        <h4 style={{ marginTop: 4 }}>
          Gönderiyi Teslim Alacak Kişinin Bilgileri:{" "}
        </h4>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div style={{ flex: 1, maxWidth: 420 }}>
            <HJSearchSelect
              url={ReceiverEndpoints.listReceivers}
              placeholder="Ad, soyad veya telefon ile alıcı ara"
              emptyText="Kayıtlı alıcı bulunamadı."
              renderOption={(row) =>
                `${row?.firstName || ""} ${row?.lastName || ""} ${
                  row?.phone || ""
                }`.trim()
              }
              onSelect={(record) =>
                applyReceiverToForm({ form, fieldName, record })
              }
            />
          </div>
          <Button
            size="small"
            type="link"
            onClick={() => openReceiverInfoModal()}
            icon={<TiContacts />}
            style={{ margin: 0, padding: 0 }}
          >
            Kayıtlı Alıcı Bilgilerim
          </Button>
        </div>
      </div>
      <RecipientForm
        form={form}
        fieldName={fieldName}
        initValues={initValues}
        onlySavedReceiversDeliveries={info?.onlySavedReceiversDeliveries}
        deliveryType={deliveryType}
      />
      <Divider />
      <h4>Sipariş Veren Bilgileri:</h4>
      <Checkbox
        checked={!visibleReceiver}
        onChange={(e) => setVisibleReceiver(!e.target.checked)}
        disabled={info?.onlySavedReceiversDeliveries}
      >
        Siparişi veren ile gönderiyi teslim alacak olan aynı kişi
      </Checkbox>
      {visibleReceiver && (
        <div style={{ marginTop: 25 }}>
          <Receiver form={form} fieldName={fieldName} />
        </div>
      )}
    </>
  );
};

export default CreateDeliveryForm;
